import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CreditCard, HardDrive, LockKeyhole, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getPublicPricingCatalog } from '../../services/pricingCatalogService';
import {
  BillingCycle,
  PLAN_ENTITLEMENTS,
  PublicPricingCatalog,
  PublicPricingPlan,
  SubscriptionPlanId,
} from '../../types/subscription';
import XpayCheckout from '../../components/subscription/XpayCheckout';
import TeacherBillingOverview from '../../components/subscription/TeacherBillingOverview';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
import Surface from '../../components/ui/Surface';
import { formatNumber } from '../../utils/localization';

const priceFor = (
  plan: PublicPricingPlan,
  cycle: BillingCycle,
  language: 'ar' | 'en',
  customOffer: string
) => {
  const price = cycle === 'yearly' ? plan.yearlyPriceEgp : plan.monthlyPriceEgp;
  return price === null
    ? customOffer
    : language === 'ar'
      ? `${formatNumber(price, language)} ج.م`
      : `EGP ${formatNumber(price, language)}`;
};

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation('subscription');
  const { language } = useLanguage();
  const {
    subscription,
    plan,
    daysUntilExpiry,
    cancelSubscription,
    loading,
    isReadOnly,
  } = useSubscription();
  const [catalog, setCatalog] = useState<PublicPricingCatalog | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [checkoutPlan, setCheckoutPlan] = useState<SubscriptionPlanId | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    getPublicPricingCatalog().then(setCatalog).catch(() => {
      toast.error(t('catalogError'));
    });
  }, [t]);

  const handleChoosePlan = (pricingPlan: PublicPricingPlan) => {
    if (pricingPlan.id === 'trial') return;
    if (pricingPlan.isContactOnly || pricingPlan.id === 'enterprise') {
      window.location.assign('/contact');
      return;
    }
    setCheckoutPlan(pricingPlan.id);
  };

  const handleCancel = async () => {
    try {
      await cancelSubscription();
      toast.success(t('cancelSuccess'));
      setShowCancelConfirm(false);
    } catch {
      toast.error(t('cancelError'));
    }
  };

  if (loading) {
    return (
      <Surface className='flex min-h-[24rem] items-center justify-center'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent' />
      </Surface>
    );
  }

  const selectedCheckoutPlan = catalog?.plans.find(item => item.id === checkoutPlan);

  return (
    <div className='mx-auto max-w-7xl space-y-6'>
      <TeacherBillingOverview />

      {isReadOnly ? (
        <Surface padding='lg' className='border border-amber-300 bg-amber-50 dark:bg-amber-950/30'>
          <div className='flex items-start gap-3'>
            <LockKeyhole className='mt-1 h-6 w-6 text-amber-700' />
            <div>
              <h2 className='font-bold text-amber-950 dark:text-amber-100'>
                {t('readOnlyTitle')}
              </h2>
              <p className='mt-1 text-sm text-amber-800 dark:text-amber-200'>
                {t('readOnlyDescription')}
              </p>
            </div>
          </div>
        </Surface>
      ) : null}

      <Surface padding='lg'>
        <div className='grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center'>
          <div className='min-w-0'>
            <div className='flex flex-wrap items-center gap-2'>
              <h1 className='text-xl font-bold leading-8 text-secondary-950 dark:text-white md:text-2xl'>
                {t('current', { plan: plan.nameAr })}
              </h1>
              <StatusBadge tone={isReadOnly ? 'warning' : 'success'}>
                {subscription?.status === 'trial' ? t('trial') : subscription?.status || t('inactive')}
              </StatusBadge>
            </div>
            <p className='mt-2 text-sm text-secondary-500 dark:text-secondary-400'>
              {daysUntilExpiry > 0
                ? t('remaining', { days: formatNumber(daysUntilExpiry, language) })
              : t('expired')}
            </p>
          </div>
          <div className='inline-flex w-full rounded-xl bg-secondary-100 p-1 dark:bg-secondary-800 sm:w-auto'>
            {(['monthly', 'yearly'] as BillingCycle[]).map(cycle => (
              <button
                key={cycle}
                onClick={() => setBillingCycle(cycle)}
                className={`min-h-10 flex-1 rounded-lg px-5 py-2 text-sm font-semibold sm:flex-none ${
                  billingCycle === cycle
                    ? 'bg-white text-primary-700 shadow dark:bg-secondary-950 dark:text-primary-300'
                    : 'text-secondary-600 dark:text-secondary-300'
                }`}
              >
                {cycle === 'monthly' ? t('monthly') : t('yearly')}
              </button>
            ))}
          </div>
        </div>
      </Surface>

      <div className='mx-auto grid max-w-5xl gap-5 md:grid-cols-3'>
        {catalog?.plans.map(pricingPlan => {
          const entitlement = PLAN_ENTITLEMENTS[pricingPlan.id];
          const isCurrent =
            pricingPlan.id === subscription?.planId &&
            billingCycle === (subscription?.billingCycle || 'monthly');
          return (
            <Surface
              key={pricingPlan.id}
              padding='lg'
              className={`relative flex min-h-[28rem] flex-col ${
                pricingPlan.isPopular ? 'border-2 border-primary-500' : ''
              }`}
            >
              {pricingPlan.isPopular ? (
                <span className='absolute -top-3 start-6 rounded-full bg-primary-600 px-3 py-1 text-xs font-bold text-white shadow-sm'>
                  {language === 'ar' ? 'الأكثر توازنًا' : 'Best fit'}
                </span>
              ) : null}

              <div className='flex min-h-[7.5rem] flex-col border-b border-secondary-100 pb-5 dark:border-secondary-800'>
                <h2 className='text-lg font-bold text-secondary-950 dark:text-white'>
                  {pricingPlan.nameAr}
                </h2>
                <p className='mt-2 text-sm leading-6 text-secondary-500'>
                  {pricingPlan.audienceAr}
                </p>
              </div>

              <div className='py-5'>
                <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                  <p className='text-2xl font-extrabold text-secondary-950 dark:text-white'>
                    {pricingPlan.id === 'trial'
                      ? t('freeTrial')
                      : priceFor(pricingPlan, billingCycle, language, t('customOffer'))}
                  </p>
                  {pricingPlan.id !== 'trial' ? (
                    <span className='text-sm font-medium text-secondary-500'>
                      / {billingCycle === 'yearly' ? t('yearlyShort') : t('monthlyShort')}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className='space-y-3 rounded-lg bg-secondary-50 p-4 text-sm dark:bg-secondary-950'>
                <div className='flex items-start gap-2'>
                  <Users className='mt-1 h-4 w-4 shrink-0 text-primary-600' />
                  <div>
                    <p className='font-bold text-secondary-950 dark:text-white'>
                      {t('activeStudentsLimit')}
                    </p>
                    <p className='mt-1 leading-6 text-secondary-600 dark:text-secondary-300'>
                      {t('studentsIncludedAndMax', {
                        included: formatNumber(pricingPlan.includedActiveStudents, language),
                        max: formatNumber(pricingPlan.maximumActiveStudents, language),
                      })}
                    </p>
                    {pricingPlan.activeStudentOverageEgp !== null ? (
                      <p className='mt-1 leading-6 text-secondary-600 dark:text-secondary-300'>
                        {t('studentBlockOverage', {
                          amount: formatNumber(pricingPlan.activeStudentOverageEgp, language),
                          block: formatNumber(pricingPlan.students.blockSize, language),
                        })}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className='flex items-start gap-2'>
                  <HardDrive className='mt-1 h-4 w-4 shrink-0 text-primary-600' />
                  <div>
                    <p className='font-bold text-secondary-950 dark:text-white'>
                      {t('storageLimit')}
                    </p>
                    <p className='mt-1 leading-6 text-secondary-600 dark:text-secondary-300'>
                      {t('storageIncluded', {
                        value: formatNumber(pricingPlan.storage.includedGb, language),
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <ul className='my-5 grow space-y-2 text-sm leading-6 text-secondary-600 dark:text-secondary-300'>
                {entitlement.featuresAr.map(feature => (
                  <li key={feature} className='flex items-start gap-2'>
                    <Check className='mt-1 h-4 w-4 shrink-0 text-success-600' />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                className='w-full'
                variant={pricingPlan.isPopular ? 'primary' : 'secondary'}
                disabled={isCurrent || pricingPlan.id === 'trial'}
                onClick={() => handleChoosePlan(pricingPlan)}
              >
                {isCurrent
                  ? t('currentPlan')
                  : pricingPlan.isContactOnly
                  ? t('contactUs')
                  : pricingPlan.id === 'trial'
                  ? t('newAccounts')
                  : t('choosePlan')}
              </Button>
            </Surface>
          );
        })}
      </div>

      {subscription && subscription.planId !== 'trial' && subscription.status !== 'cancelled' ? (
        <Surface padding='lg'>
          {!showCancelConfirm ? (
            <Button variant='danger' onClick={() => setShowCancelConfirm(true)}>
              {t('cancelSubscription')}
            </Button>
          ) : (
            <div className='space-y-4'>
              <p className='text-sm text-secondary-700 dark:text-secondary-200'>
                {t('cancelWarning')}
              </p>
              <div className='flex gap-2'>
                <Button variant='secondary' onClick={() => setShowCancelConfirm(false)}>
                  {t('goBack')}
                </Button>
                <Button variant='danger' onClick={handleCancel}>
                  {t('confirmCancel')}
                </Button>
              </div>
            </div>
          )}
        </Surface>
      ) : null}

      <Modal
        isOpen={Boolean(selectedCheckoutPlan)}
        onClose={() => setCheckoutPlan(null)}
        title={t('checkoutTitle')}
        description={
          selectedCheckoutPlan
            ? `${selectedCheckoutPlan.nameAr} · ${
                billingCycle === 'yearly' ? t('yearlyShort') : t('monthlyShort')
              }`
            : undefined
        }
        size='md'
      >
        {selectedCheckoutPlan && checkoutPlan ? (
          <div className='space-y-5'>
            <Surface padding='md' className='bg-secondary-50 dark:bg-secondary-950'>
              <div className='flex items-center justify-between gap-3'>
                <span className='flex items-center gap-2 text-sm text-secondary-500'>
                  <CreditCard className='h-4 w-4' /> {t('fixedAmount')}
                </span>
                <strong className='text-xl'>
                  {priceFor(selectedCheckoutPlan, billingCycle, language, t('customOffer'))}
                </strong>
              </div>
              <p className='mt-2 text-xs text-secondary-500'>
                {t('xpayHint')}
              </p>
              {billingCycle === 'yearly' ? (
                <p className='mt-2 text-xs leading-5 text-amber-700'>
                  {t('annualUsageNote')}
                </p>
              ) : null}
            </Surface>
            <XpayCheckout
              planId={checkoutPlan}
              billingCycle={billingCycle}
              onError={() => toast.error(t('paymentError'))}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default SubscriptionPage;
